import * as React from "react";
import { useHotkeys } from "react-hotkeys-hook";
import * as _ from "lodash";
import styled, { css } from "styled-components";
import { DebounceInput } from "react-debounce-input";

import { InputCss } from "src/library/components/Forms/Input";
import Grid from "src/library/components/Hedron";
import { Icon } from "src/library/components/Icon";
import { Icons } from "src/library/components/Icon/types";
import { ProductFilterParams } from "src/core/products/store/products.reducer";
import { gtmSearchEvent } from "src/library/utils/tagmanager-events/helpers/gtm-search-event";

interface ProductSearchFilterProps {
  handleSearch: (searchFilter: ProductFilterParams) => void;
}

const ProductSearchFilter: React.ReactFCWithChildren<ProductSearchFilterProps> = ({ handleSearch }) => {
  const onSearch = React.useCallback(
    (searchString: string) => {
      const filters: ProductFilterParams = { $or: [ { description: { $like: `%${searchString}%` } }, { partNumber: { $like: `%${searchString}%` } } ] };

      gtmSearchEvent("product", searchString);
      handleSearch(filters);
    },
    [ handleSearch ]
  );

  const inputRef = React.useRef<HTMLInputElement>(null);

  /// hot key for control + s to search
  useHotkeys("ctrl+s", () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  });

  return (
    <SearchWrapper>
      <DebounceInput
        inputRef={inputRef}
        className="search-input"
        placeholder="Search products... (ctrl+s)"
        debounceTimeout={1000}
        minLength={2}
        onChange={(e: React.ChangeEvent<any>) => onSearch(e.target.value)}
      />

      <Icon
        icon={Icons.SEARCH}
        $fillColor="lightGrey"
      />
    </SearchWrapper>
  );
};

const SearchWrapper = styled(Grid.Box)`
  ${({ theme }) => css`
    margin-right: ${theme.spacing.margins.md};
    position: relative;

    &:hover {
      svg {
        fill: ${theme.colors.lightGrey};
      }
    }
    .search-input {
      ${InputCss};
      width: 220px;
      border: solid 2px ${theme.colors.lightestGrey};
      border-radius: ${theme.borderRadius};
      padding-left: 35px;
      transition: ${theme.transition.quickest};
      font-size: ${theme.fonts.sizes.p};

      &:hover,
      &:focus {
        border-color: ${theme.colors.primaryDark};
        background-color: ${theme.colors.white};
      }
    }

    svg {
      transition: ${theme.transition.quickest};
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      left: 5px;
    }
  `}
`;

export default ProductSearchFilter;
